/* UGC Gallery + Fullscreen Player — replaces Tolstoy */

if (!customElements.get('ugc-gallery')) {
  class UgcGallery extends HTMLElement {
    connectedCallback() {
      this.posts = [];
      try {
        const dataEl = this.querySelector('[data-ugc-posts]');
        if (dataEl) this.posts = JSON.parse(dataEl.textContent);
        /* Auto-detect media_type from URL extension to guard against data entry errors */
        this.posts.forEach((p) => {
          const url = (p.media_url || '').split('?')[0].toLowerCase();
          if (/\.(mp4|webm|mov|m3u8)$/.test(url)) p.media_type = 'video';
        });
      } catch (e) { /* graceful fallback */ }

      this.strip = this.querySelector('.ugc-gallery__strip');
      this.player = this.querySelector('ugc-player');
      this.prevBtn = this.querySelector('.ugc-gallery__nav-btn--prev');
      this.nextBtn = this.querySelector('.ugc-gallery__nav-btn--next');

      this.bindThumbnails();
      this.bindStripNav();
      this.observeImpression();
    }

    observeImpression() {
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          this.trackGalleryEvent('ugc_impression', {
            post_count: this.posts.length
          });
        });
      }, { threshold: 0.3 });
      observer.observe(this);
    }

    trackGalleryEvent(name, data = {}) {
      let productData = null;
      try {
        const el = this.querySelector('[data-ugc-product]');
        if (el) productData = JSON.parse(el.textContent);
      } catch (e) { /* */ }

      const payload = {
        product_title: productData?.title || '',
        ...data
      };

      if (typeof gtag === 'function') {
        gtag('event', name, payload);
      }

      const _learnq = window._learnq;
      if (_learnq && typeof _learnq.push === 'function') {
        _learnq.push(['track', name, payload]);
      }
    }

    bindThumbnails() {
      this.thumbs = this.querySelectorAll('.ugc-gallery__thumb');
      this.thumbs.forEach((thumb, i) => {
        thumb.dataset.index = i;
        thumb.addEventListener('click', () => {
          if (this.player) this.player.open(i, this);
        });
      });
    }

    highlightThumb(index) {
      if (!this.thumbs) return;
      this.thumbs.forEach((t, i) => {
        t.classList.toggle('ugc-gallery__thumb--active', i === index);
      });
    }

    bindStripNav() {
      if (!this.strip || !this.prevBtn || !this.nextBtn) return;

      const updateButtons = () => {
        const { scrollLeft, scrollWidth, clientWidth } = this.strip;
        this.prevBtn.disabled = scrollLeft <= 4;
        this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 4;
      };

      this.prevBtn.addEventListener('click', () => {
        this.strip.scrollBy({ left: -200, behavior: 'smooth' });
      });
      this.nextBtn.addEventListener('click', () => {
        this.strip.scrollBy({ left: 200, behavior: 'smooth' });
      });

      this.strip.addEventListener('scroll', updateButtons, { passive: true });
      updateButtons();
      requestAnimationFrame(updateButtons);
    }

  }

  customElements.define('ugc-gallery', UgcGallery);
}

if (!customElements.get('ugc-player')) {
  class UgcPlayer extends HTMLElement {
    connectedCallback() {
      this.posts = [];
      this.productData = null;
      this.currentIndex = 0;
      this.isMuted = true;

      try {
        const gallery = this.closest('ugc-gallery');
        const dataEl = gallery?.querySelector('[data-ugc-posts]');
        if (dataEl) {
          this.posts = JSON.parse(dataEl.textContent);
          /* Auto-detect media_type from URL extension */
          this.posts.forEach((p) => {
            const url = (p.media_url || '').split('?')[0].toLowerCase();
            if (/\.(mp4|webm|mov|m3u8)$/.test(url)) p.media_type = 'video';
          });
        }
        const productEl = gallery?.querySelector('[data-ugc-product]');
        if (productEl) this.productData = JSON.parse(productEl.textContent);
      } catch (e) { /* graceful fallback */ }

      this.mediaContainer = this.querySelector('.ugc-player__media-container');
      this.mediaWrapper = this.querySelector('.ugc-player__media-wrapper');
      this.handleEl = this.querySelector('.ugc-player__handle');
      this.counterContainer = this.querySelector('.ugc-player__counter');

      this.querySelector('.ugc-player__close')?.addEventListener('click', () => this.close());
      this.querySelector('.ugc-player__nav--prev')?.addEventListener('click', () => this.navigate(-1));
      this.querySelector('.ugc-player__nav--next')?.addEventListener('click', () => this.navigate(1));
      this.querySelector('.ugc-player__mute')?.addEventListener('click', () => this.toggleMute());
      this.querySelector('.ugc-player__play-pause')?.addEventListener('click', () => this.togglePlay());
      this.querySelector('[data-ugc-atc]')?.addEventListener('click', () => this.addToCart());

      this.addEventListener('click', (e) => {
        if (e.target === this) this.close();
      });

      this.bindKeyboard();
      this.bindSwipe();

      /* Restore scroll if page is restored from bfcache */
      this._pageShowHandler = (e) => {
        if (e.persisted && !this.hasAttribute('open')) {
          document.body.style.overflow = '';
        }
      };
      window.addEventListener('pageshow', this._pageShowHandler);
    }

    open(index, gallery) {
      this.currentIndex = index;
      this.gallery = gallery || this.closest('ugc-gallery');
      this.isMuted = true;
      this.renderMedia();
      this.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      if (this.gallery && this.gallery.highlightThumb) this.gallery.highlightThumb(index);
      this.focus();
      this.trapFocus();
      this.trackEvent('ugc_open', { index });
    }

    close() {
      this.pauseCurrentMedia();
      this.isMuted = true;
      this.removeAttribute('open');
      document.body.style.overflow = '';
      this.releaseFocus();
      if (this.gallery && this.gallery.highlightThumb) {
        this.gallery.querySelectorAll('.ugc-gallery__thumb--active').forEach(t => t.classList.remove('ugc-gallery__thumb--active'));
      }
    }

    navigate(dir) {
      const newIndex = this.currentIndex + dir;
      if (newIndex < 0 || newIndex >= this.posts.length) return;
      this.pauseCurrentMedia();
      this.currentIndex = newIndex;
      this.isMuted = true;
      this.renderMedia();
      if (this.gallery && this.gallery.highlightThumb) this.gallery.highlightThumb(newIndex);
      this.trackEvent('ugc_navigate', { index: newIndex, direction: dir > 0 ? 'next' : 'prev' });
    }

    renderMedia() {
      if (!this.mediaContainer || !this.posts.length) return;
      const post = this.posts[this.currentIndex];
      if (!post) return;

      const mediaUrl = post.media_url || post.image;
      const mediaType = post.media_type || 'image';
      const thumb = post.thumbnail_url || post.image;
      const handle = post.handle || '';
      const platform = post.platform || 'instagram';

      /* Clean up existing media to avoid listener leaks */
      const oldVideo = this.mediaContainer.querySelector('video');
      if (oldVideo) {
        oldVideo.pause();
        oldVideo.removeAttribute('src');
        oldVideo.load();
      }
      this.mediaContainer.innerHTML = '';

      const declaredRatio = post.aspect_ratio || '';
      if (declaredRatio && this.mediaWrapper) {
        this.mediaWrapper.setAttribute('data-ratio', declaredRatio);
      }

      const detectRatio = (w, h) => {
        if (!w || !h) return;
        const r = w / h;
        let best = '4:5';
        if (r < 0.65) best = '9:16';
        else if (r > 0.9) best = '1:1';
        if (this.mediaWrapper) this.mediaWrapper.setAttribute('data-ratio', best);
      };

      if (mediaType === 'video') {
        const video = document.createElement('video');
        video.setAttribute('muted', '');
        video.muted = true;
        video.volume = 0;
        video.playsInline = true;
        video.controls = false;
        video.loop = true;
        video.poster = thumb || '';
        video.preload = 'auto';
        if (!declaredRatio) {
          video.addEventListener('loadedmetadata', () => detectRatio(video.videoWidth, video.videoHeight), { once: true });
        }
        video.addEventListener('error', () => {
          this.mediaContainer.innerHTML = '<div style="color:#fff;padding:2rem;text-align:center">Video unavailable</div>';
        });
        video.addEventListener('click', () => this.togglePlay());
        video.addEventListener('play', () => this.updatePlayPauseIcon());
        video.addEventListener('pause', () => this.updatePlayPauseIcon());
        this.mediaContainer.appendChild(video);
        video.src = mediaUrl;
        video.muted = true;
        video.volume = 0;
      } else {
        const img = document.createElement('img');
        img.src = mediaUrl;
        img.alt = post.caption || post.text || '';
        img.loading = 'eager';
        if (!declaredRatio) {
          img.addEventListener('load', () => detectRatio(img.naturalWidth, img.naturalHeight), { once: true });
        }
        img.addEventListener('error', () => {
          this.mediaContainer.innerHTML = '<div style="color:#fff;padding:2rem;text-align:center">Image unavailable</div>';
        });
        this.mediaContainer.appendChild(img);
      }

      if (this.handleEl) {
        if (handle) {
          this.handleEl.style.display = '';
          const iconHtml = platform === 'tiktok'
            ? '<svg viewBox="0 0 448 512" fill="currentColor"><path d="M448 209.9a210.1 210.1 0 01-122.8-39.3v178.8A162.6 162.6 0 11185 188.3v89.9a74.6 74.6 0 1052.2 71.2V0h88a121 121 0 00122.8 121z"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg>';
          const span = document.createElement('span');
          span.textContent = handle;
          this.handleEl.innerHTML = iconHtml;
          this.handleEl.appendChild(span);
        } else {
          this.handleEl.style.display = 'none';
        }
      }

      if (this.counterContainer) {
        this.counterContainer.innerHTML = this.posts.map((_, i) =>
          '<div class="ugc-player__counter-dot' + (i === this.currentIndex ? ' ugc-player__counter-dot--active' : '') + '"></div>'
        ).join('');
      }

      const prevBtn = this.querySelector('.ugc-player__nav--prev');
      const nextBtn = this.querySelector('.ugc-player__nav--next');
      if (prevBtn) prevBtn.disabled = this.currentIndex === 0;
      if (nextBtn) nextBtn.disabled = this.currentIndex === this.posts.length - 1;

      const muteBtn = this.querySelector('.ugc-player__mute');
      if (muteBtn) {
        muteBtn.style.display = mediaType === 'video' ? '' : 'none';
        this.updateMuteIcon();
      }

      const playPauseBtn = this.querySelector('.ugc-player__play-pause');
      if (playPauseBtn) {
        playPauseBtn.style.display = mediaType === 'video' ? '' : 'none';
        this.updatePlayPauseIcon();
      }

      const atcBtn = this.querySelector('[data-ugc-atc]');
      if (atcBtn && this.productData) {
        if (!this.productData.has_only_default_variant) {
          atcBtn.textContent = 'Shop';
        } else if (!this.productData.available) {
          atcBtn.textContent = 'Sold Out';
          atcBtn.disabled = true;
        } else {
          atcBtn.textContent = 'Add to Cart';
          atcBtn.disabled = false;
        }
      }
    }

    addToCart() {
      if (!this.productData || this._atcPending) return;
      const btn = this.querySelector('[data-ugc-atc]');
      if (!btn || btn.disabled) return;

      if (!this.productData.has_only_default_variant) {
        window.location.href = this.productData.url;
        return;
      }

      if (!this.productData.available) return;

      this._atcPending = true;
      btn.disabled = true;
      btn.textContent = 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: this.productData.variant_id, quantity: 1 }] })
      })
        .then((res) => {
          if (!res.ok) throw new Error(res.statusText);
          btn.textContent = 'Added!';
          this.trackEvent('ugc_add_to_cart', { variant_id: this.productData.variant_id });
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            setTimeout(() => cartDrawer.open(), 300);
          }
          setTimeout(() => { btn.textContent = 'Add to Cart'; btn.disabled = false; }, 2000);
        })
        .catch(() => {
          btn.textContent = 'Add to Cart';
          btn.disabled = false;
        })
        .finally(() => {
          this._atcPending = false;
        });
    }

    trackEvent(name, data = {}) {
      const post = this.posts[this.currentIndex] || {};
      const payload = {
        media_type: post.media_type || 'image',
        handle: post.handle || '',
        platform: post.platform || 'instagram',
        product_title: this.productData?.title || '',
        ...data
      };

      if (typeof gtag === 'function') {
        gtag('event', name, payload);
      }

      const _learnq = window._learnq;
      if (_learnq && typeof _learnq.push === 'function') {
        _learnq.push(['track', name, payload]);
      }
    }

    pauseCurrentMedia() {
      const video = this.mediaContainer?.querySelector('video');
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    }

    toggleMute() {
      this.isMuted = !this.isMuted;
      const video = this.mediaContainer?.querySelector('video');
      if (video) {
        video.muted = this.isMuted;
        video.volume = this.isMuted ? 0 : 1;
        if (this.isMuted) {
          video.setAttribute('muted', '');
        } else {
          video.removeAttribute('muted');
        }
      }
      this.updateMuteIcon();
    }

    updateMuteIcon() {
      const muteBtn = this.querySelector('.ugc-player__mute');
      if (!muteBtn) return;
      muteBtn.innerHTML = this.isMuted
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>';
    }

    togglePlay() {
      const video = this.mediaContainer?.querySelector('video');
      if (!video) return;
      if (video.paused) {
        video.muted = this.isMuted;
        video.volume = this.isMuted ? 0 : 1;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
      this.updatePlayPauseIcon();
    }

    updatePlayPauseIcon() {
      const btn = this.querySelector('.ugc-player__play-pause');
      if (!btn) return;
      const video = this.mediaContainer?.querySelector('video');
      const paused = !video || video.paused;
      btn.innerHTML = paused
        ? '<svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><polygon points="5,3 19,12 5,21"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><rect x="5" y="3" width="4" height="18"/><rect x="15" y="3" width="4" height="18"/></svg>';
    }

    trapFocus() {
      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;
        const focusable = this.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      this.addEventListener('keydown', this._focusTrapHandler);
    }

    releaseFocus() {
      if (this._focusTrapHandler) {
        this.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
    }

    disconnectedCallback() {
      if (this._keyHandler) document.removeEventListener('keydown', this._keyHandler);
      if (this._pageShowHandler) window.removeEventListener('pageshow', this._pageShowHandler);
      this.releaseFocus();
      if (this.hasAttribute('open')) {
        document.body.style.overflow = '';
      }
    }

    bindKeyboard() {
      this._keyHandler = (e) => {
        if (!this.hasAttribute('open')) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowLeft') this.navigate(-1);
        if (e.key === 'ArrowRight') this.navigate(1);
        if (e.key === 'm' || e.key === 'M') this.toggleMute();
        if (e.key === ' ') {
          const post = this.posts[this.currentIndex];
          if (post && (post.media_type === 'video')) {
            e.preventDefault();
            this.togglePlay();
          }
        }
      };
      document.addEventListener('keydown', this._keyHandler);
    }

    bindSwipe() {
      let startX = 0;
      let startY = 0;

      this.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      this.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.navigate(dx > 0 ? -1 : 1);
        }
      }, { passive: true });
    }
  }

  customElements.define('ugc-player', UgcPlayer);
}
